import { useState } from 'react';
import { CallPlayer } from './CallPlayer';
import { startCallJob } from '../api';
import type { Call } from '../types';

interface Props {
  calls: Call[];
  token: string;
  onRefresh: () => void | Promise<void>;
  /** Called after a call was started, so the recordings list picks up the job. */
  onStarted: () => void | Promise<void>;
}

function formatDuration(seconds: number): string {
  const hh = Math.floor(seconds / 3600);
  const mm = Math.round((seconds % 3600) / 60);
  return hh > 0 ? `${hh} h ${mm} min` : `${Math.max(1, mm)} min`;
}

/**
 * What transcribing this call would submit to speech recognition.
 *
 * Every participant is recorded for the whole time they are present, mostly in
 * silence, and each of those tracks is billed in full - a five-person hour is
 * five hours. Shown before the button rather than on the invoice afterwards.
 */
function formatCost(billableSeconds: number): string {
  const euros = (billableSeconds / 60) * 0.024 * 0.92;
  return euros < 0.01 ? '< 0,01 €' : `${euros.toFixed(2).replace('.', ',')} €`;
}

export function CallList({ calls, token, onRefresh, onStarted }: Props) {
  const [starting, setStarting] = useState<string>();
  const [watching, setWatching] = useState<string>();
  const [error, setError] = useState<string>();

  async function start(call: Call) {
    setError(undefined);
    setStarting(call.roomId);
    try {
      await startCallJob(token, call.roomId);
      // Both lists change: the call is marked, the protocol appears.
      await Promise.all([onRefresh(), onStarted()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Auswertung konnte nicht gestartet werden');
    } finally {
      setStarting(undefined);
    }
  }

  return (
    <section>
      <div className="section-head">
        <h2>Videokonferenzen</h2>
        <button onClick={() => void onRefresh()}>Aktualisieren</button>
      </div>

      <p className="muted small section-note">
        Aufzeichnungen aus meet.myuniq.ai. Neue Konferenzen erscheinen hier von selbst; ausgewertet
        wird erst, wenn du eine auswählst. Jede Person wird einzeln aufgezeichnet, daher die
        Kostenangabe.
      </p>

      {error && <p className="error">{error}</p>}

      {calls.length === 0 ? (
        <p className="muted">Noch keine Aufzeichnungen gefunden.</p>
      ) : (
        <ul className="job-list">
          {calls.map((call) => {
            const started = new Date(call.startedAt);
            return (
              <li key={call.roomId} className="job">
                <div className="job-head call-head">
                  <span className="job-title">
                    <strong>{call.roomName}</strong>
                    <span className="muted small"> · {started.toLocaleString('de-DE')}</span>
                  </span>
                  <span className="artifact-actions">
                    {(call.media ?? []).some((m) => m.kind === 'video') && (
                      <button
                        onClick={() => setWatching(watching === call.roomId ? undefined : call.roomId)}
                      >
                        {watching === call.roomId ? 'Video schließen' : 'Video ansehen'}
                      </button>
                    )}
                  {call.jobId ? (
                    <span className="badge badge-completed">Ausgewertet</span>
                  ) : (
                    <button
                      className="primary"
                      disabled={starting !== undefined}
                      onClick={() => void start(call)}
                    >
                      {starting === call.roomId ? 'Wird gestartet …' : 'Auswerten'}
                    </button>
                  )}
                  </span>
                </div>

                <p className="muted small job-meta">
                  {formatDuration(call.durationSeconds)} · {call.participants.length} Teilnehmende ·{' '}
                  {call.tracks.length} Tonspuren · Auswertung ca. {formatCost(call.billableSeconds)}
                </p>
                <p className="muted small job-meta">{call.participants.join(', ')}</p>

                {watching === call.roomId && <CallPlayer call={call} token={token} />}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
